import { FormDatePicker, FormInput, FormSelect, Loader } from "@/components";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useFinancialReporting from "./useFinancialReporting";
import {
  AS_ON,
  CURRENT_RATIO,
  DE,
  EBITDA,
  EBITDA_MARGIN,
  GROSS_BLOCK,
  PAT,
  PAT_MARGIN,
  ROCE,
  RONW,
  TOTAL_DEBT,
  TURN_OVER,
  YEAR,
} from "./consts";
import { cn, yearOptions } from "@/lib/utils";

const FinancialReporting = () => {
  const {
    form,
    onSubmit,
    requiredFields,
    isLoadingFinance,
    isAddPending,
    yearKey,
  } = useFinancialReporting();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      {isLoadingFinance ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white rounded-sm px-[30px] py-[25px]">
              {Object.entries(form.watch())?.map(
                ([key, _]: [string, any], index: number) => (
                  <div
                    key={key}
                    className={cn(
                      "pb-6",
                      index != Object.entries(form.watch())?.length - 1
                        ? "border-b border-g-200 mb-6"
                        : ""
                    )}
                  >
                    <span className="text-[16px] font-semibold text-primary">
                      {key}
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-[20px] mt-3">
                      <FormSelect
                        key={yearKey}
                        name={`${key}.${YEAR}`}
                        control={form.control}
                        label="Year"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[YEAR]?.message}
                        isRequired={requiredFields.includes(`${key}.${YEAR}`)}
                        options={yearOptions(5)}
                      />
                      <FormDatePicker
                        name={`${key}.${AS_ON}`}
                        label="As On"
                        placeholder="Please Select"
                        error={
                          (errors as any)?.[key]?.[AS_ON]?.message as string
                        }
                        isRequired
                      />
                      <FormInput
                        name={`${key}.${TURN_OVER}`}
                        control={form.control}
                        label="Turn Over(in Cr)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[TURN_OVER]?.message}
                        isRequired={requiredFields.includes(
                          `${key}.${TURN_OVER}`
                        )}
                        variant="float"
                        startIcon="₹"
                      />
                      <FormInput
                        name={`${key}.${EBITDA}`}
                        control={form.control}
                        label="EBITDA (PBDIT)(in Cr)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[EBITDA]?.message}
                        isRequired={requiredFields.includes(`${key}.${EBITDA}`)}
                        variant="float"
                        startIcon="₹"
                      />
                      <FormInput
                        name={`${key}.${EBITDA_MARGIN}`}
                        control={form.control}
                        label="EBITDA Margin(in %)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[EBITDA_MARGIN]?.message}
                        isRequired={requiredFields.includes(
                          `${key}.${EBITDA_MARGIN}`
                        )}
                        variant="float"
                      />
                      <FormInput
                        name={`${key}.${PAT}`}
                        control={form.control}
                        label="PAT(in Cr)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[PAT]?.message}
                        isRequired={requiredFields.includes(`${key}.${PAT}`)}
                        variant="float"
                        startIcon="₹"
                      />
                      <FormInput
                        name={`${key}.${PAT_MARGIN}`}
                        control={form.control}
                        label="PAT Margin(in %)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[PAT_MARGIN]?.message}
                        isRequired={requiredFields.includes(
                          `${key}.${PAT_MARGIN}`
                        )}
                        variant="float"
                      />
                      <FormInput
                        name={`${key}.${GROSS_BLOCK}`}
                        control={form.control}
                        label="Gross Block(in Cr)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[GROSS_BLOCK]?.message}
                        isRequired={requiredFields.includes(
                          `${key}.${GROSS_BLOCK}`
                        )}
                        variant="float"
                        startIcon="₹"
                      />
                      <FormInput
                        name={`${key}.${DE}`}
                        control={form.control}
                        label="D/E"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[DE]?.message}
                        isRequired={requiredFields.includes(`${key}.${DE}`)}
                        variant="float"
                      />
                      <FormInput
                        name={`${key}.${ROCE}`}
                        control={form.control}
                        label="ROCE(in %)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[ROCE]?.message}
                        isRequired={requiredFields.includes(`${key}.${ROCE}`)}
                        variant="float"
                      />
                      <FormInput
                        name={`${key}.${CURRENT_RATIO}`}
                        control={form.control}
                        label="Current Ratio"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[CURRENT_RATIO]?.message}
                        isRequired={requiredFields.includes(
                          `${key}.${CURRENT_RATIO}`
                        )}
                        variant="float"
                      />
                      <FormInput
                        name={`${key}.${TOTAL_DEBT}`}
                        control={form.control}
                        label="Total Debt(in Cr)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[TOTAL_DEBT]?.message}
                        isRequired={requiredFields.includes(
                          `${key}.${TOTAL_DEBT}`
                        )}
                        variant="float"
                        startIcon="₹"
                      />
                      <FormInput
                        name={`${key}.${RONW}`}
                        control={form.control}
                        label="RONW(in %)"
                        placeholder="Please enter"
                        error={(errors as any)[key]?.[RONW]?.message}
                        isRequired={requiredFields.includes(`${key}.${RONW}`)}
                        variant="float"
                      />
                    </div>
                  </div>
                )
              )}

              <div className="flex flex-row justify-end gap-3  py-3">
                <Button type="submit" disabled={isAddPending}>
                  {isAddPending ? <Loader variant="secondary" /> : <></>}
                  Submit
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export default FinancialReporting;
